import { ReplayEngine } from '../replay/ReplayEngine';
import { ReplayCameraMode } from '../replay/ReplayCameras';

export class ReplayUI {
  public container: HTMLElement;
  private playPauseBtn!: HTMLButtonElement;
  private timelineSlider!: HTMLInputElement;
  private timeLabel!: HTMLElement;
  private speedBtns: HTMLButtonElement[] = [];
  private camBtns: { mode: ReplayCameraMode; btn: HTMLButtonElement }[] = [];
  private telemetryReadout!: HTMLElement;

  private onExitCallback: (() => void) | null = null;
  private onRetryCallback: (() => void) | null = null;

  constructor(private replayEngine: ReplayEngine) {
    this.container = document.createElement('div');
    this.container.id = 'replay-ui-deck';
    this.container.style.cssText = `
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      background: linear-gradient(180deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0.95) 30%);
      padding: 24px 20px 28px 20px;
      z-index: 30;
      display: flex;
      flex-direction: column;
      gap: 14px;
      pointer-events: auto;
    `;

    this.buildUI();
  }

  private buildUI() {
    // 1. Top Row: Telemetry readout & Camera Selector (3 perspectives)
    const topRow = document.createElement('div');
    topRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;
    `;

    // 3 Camera Switcher
    const camGroup = document.createElement('div');
    camGroup.style.cssText = `display: flex; gap: 8px;`;

    const cameras: { mode: ReplayCameraMode; label: string }[] = [
      { mode: 'cockpit', label: '🎥 Cockpit (Lenkrad)' },
      { mode: 'birdseye', label: '🦅 Vogelperspektive' },
      { mode: 'spectator', label: '🚶 Zuschauer (Straße)' },
    ];

    cameras.forEach((c) => {
      const btn = document.createElement('button');
      btn.innerText = c.label;
      btn.style.cssText = `
        background: ${c.mode === 'cockpit' ? '#2563eb' : '#1e293b'};
        color: white;
        border: 1px solid ${c.mode === 'cockpit' ? '#60a5fa' : '#475569'};
        padding: 8px 14px;
        border-radius: 8px;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
      `;
      btn.addEventListener('click', () => {
        this.replayEngine.setCameraMode(c.mode);
        this.camBtns.forEach((b) => {
          b.btn.style.background = b.mode === c.mode ? '#2563eb' : '#1e293b';
          b.btn.style.borderColor = b.mode === c.mode ? '#60a5fa' : '#475569';
        });
      });
      this.camBtns.push({ mode: c.mode, btn });
      camGroup.appendChild(btn);
    });

    topRow.appendChild(camGroup);

    // Live Telemetry Readout
    this.telemetryReadout = document.createElement('div');
    this.telemetryReadout.style.cssText = `
      font-family: 'Chakra Petch', sans-serif;
      font-size: 14px;
      font-weight: 700;
      color: #38bdf8;
      background: rgba(0, 0, 0, 0.4);
      padding: 6px 14px;
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.1);
    `;
    this.telemetryReadout.innerText = '0 KM/H | LENKWINKEL: 0° | GANG: D';
    topRow.appendChild(this.telemetryReadout);

    this.container.appendChild(topRow);

    // 2. Middle Row: Timeline Scrubber
    const timelineRow = document.createElement('div');
    timelineRow.style.cssText = `
      display: flex;
      align-items: center;
      gap: 14px;
      width: 100%;
    `;

    this.playPauseBtn = document.createElement('button');
    this.playPauseBtn.innerHTML = '⏸️';
    this.playPauseBtn.style.cssText = `
      background: #3b82f6;
      color: white;
      border: none;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      font-size: 18px;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
    `;
    this.playPauseBtn.addEventListener('click', () => {
      if (this.replayEngine.isPlaying) {
        this.replayEngine.pause();
        this.playPauseBtn.innerHTML = '▶️';
      } else {
        this.replayEngine.resume();
        this.playPauseBtn.innerHTML = '⏸️';
      }
    });

    this.timelineSlider = document.createElement('input');
    this.timelineSlider.type = 'range';
    this.timelineSlider.min = '0';
    this.timelineSlider.max = '1000';
    this.timelineSlider.value = '0';
    this.timelineSlider.style.cssText = `
      flex-grow: 1;
      height: 8px;
      border-radius: 4px;
      background: #334155;
      outline: none;
      cursor: pointer;
      accent-color: #38bdf8;
    `;
    this.timelineSlider.addEventListener('input', () => {
      const progress = parseInt(this.timelineSlider.value, 10) / 1000;
      this.replayEngine.seek(progress);
    });

    this.timeLabel = document.createElement('span');
    this.timeLabel.style.cssText = `
      font-family: 'Chakra Petch', sans-serif;
      font-size: 14px;
      color: #94a3b8;
      min-width: 65px;
      text-align: right;
    `;
    this.timeLabel.innerText = '00:00';

    timelineRow.appendChild(this.playPauseBtn);
    timelineRow.appendChild(this.timelineSlider);
    timelineRow.appendChild(this.timeLabel);
    this.container.appendChild(timelineRow);

    // 3. Bottom Row: Speed Buttons & Action Controls (Exit / Retry)
    const bottomRow = document.createElement('div');
    bottomRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
    `;

    // Speed buttons (0.5x, 1x, 2x)
    const speedGroup = document.createElement('div');
    speedGroup.style.cssText = `display: flex; gap: 6px;`;

    [0.5, 1.0, 2.0].forEach((spd) => {
      const btn = document.createElement('button');
      btn.innerText = `${spd}x`;
      btn.style.cssText = `
        background: ${spd === 1.0 ? '#475569' : '#1e293b'};
        color: white;
        border: 1px solid #64748b;
        padding: 4px 10px;
        border-radius: 6px;
        font-weight: 700;
        font-size: 12px;
        cursor: pointer;
      `;
      btn.addEventListener('click', () => {
        this.replayEngine.setSpeed(spd);
        this.speedBtns.forEach((b) => {
          b.style.background = b === btn ? '#475569' : '#1e293b';
        });
      });
      this.speedBtns.push(btn);
      speedGroup.appendChild(btn);
    });

    // Exit / Retry Buttons
    const actionGroup = document.createElement('div');
    actionGroup.style.cssText = `display: flex; gap: 10px;`;

    const btnRetry = document.createElement('button');
    btnRetry.innerText = '🔄 Erneut versuchen';
    btnRetry.style.cssText = `
      background: #16a34a;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 13px;
      cursor: pointer;
    `;
    btnRetry.addEventListener('click', () => {
      if (this.onRetryCallback) this.onRetryCallback();
    });

    const btnExit = document.createElement('button');
    btnExit.innerText = '❌ Replay beenden';
    btnExit.style.cssText = `
      background: #dc2626;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 13px;
      cursor: pointer;
    `;
    btnExit.addEventListener('click', () => {
      if (this.onExitCallback) this.onExitCallback();
    });

    actionGroup.appendChild(btnRetry);
    actionGroup.appendChild(btnExit);

    bottomRow.appendChild(speedGroup);
    bottomRow.appendChild(actionGroup);
    this.container.appendChild(bottomRow);
  }

  public update(frame: import('../replay/TelemetryRecorder').TelemetryFrame | null) {
    if (!frame) return;

    // Update Slider & Time Label
    const progress = this.replayEngine.getProgress();
    this.timelineSlider.value = `${Math.round(progress * 1000)}`;

    const totalSeconds = Math.floor(frame.time);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    this.timeLabel.innerText = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

    // Update Telemetry Bar
    const steerDeg = Math.round((frame.steeringAngle * 180) / Math.PI);
    const infStr = frame.infractionTitle ? ` | 🚨 ${frame.infractionTitle}` : '';
    this.telemetryReadout.innerText = `${Math.round(frame.speedKmh)} KM/H | LENKWINKEL: ${steerDeg}° | GANG: ${frame.gear}${infStr}`;
  }

  public onExit(cb: () => void) {
    this.onExitCallback = cb;
  }

  public onRetry(cb: () => void) {
    this.onRetryCallback = cb;
  }

  public mount(root: HTMLElement) {
    root.appendChild(this.container);
  }

  public unmount() {
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }
}
